import React, { useState, useEffect, useCallback } from 'react';
import { getCreditScore } from '../utils/creditScore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import ExportToolbar from '../components/ExportToolbar';
import { exportCSV, exportJSON, todayStr } from '../utils/exportUtils';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt$(v) {
  if (v == null || isNaN(v) || v === 0) return '—';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '$' + Math.round(v / 1e3) + 'K';
  return '$' + Math.round(v).toLocaleString();
}
function fmtFull$(v) {
  if (!v || isNaN(v) || v === 0) return '—';
  return '$' + Math.round(parseFloat(v)).toLocaleString();
}
function fmtPct(v, d = 1) {
  if (v == null || isNaN(v)) return '—';
  return parseFloat(v).toFixed(d) + '%';
}

function ltvColor(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '#374151';
  if (n < 80) return '#acf44e';
  if (n <= 90) return '#d97706';
  return '#ff082f';
}

const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const th = { padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' };
const td = { padding: '11px 12px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f9fafb', whiteSpace: 'nowrap' };
const tdTotal = { padding: '11px 12px', fontSize: '13px', fontWeight: '700', color: '#111827', borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb', whiteSpace: 'nowrap' };

const QUICK_RANGES = [
  { label: 'All Time', days: null },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'Last 6mo', days: 180 },
  { label: 'Last 12mo', days: 365 },
];

function RevenueCard({ title, amount, note, color }) {
  const colors = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', text: '#3a6ad9', label: '#374151' },
    purple: { bg: '#edf1fb', border: '#c5d3f5', text: '#3a6ad9', label: '#374151' },
    green:  { bg: '#f0fde4', border: '#d4f9a0', text: '#acf44e', label: '#374151' },
  }[color] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', label: '#374151' };

  return (
    <div style={{ ...card, backgroundColor: colors.bg, border: `1px solid ${colors.border}`, flex: 1 }}>
      <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.label, marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '28px', fontWeight: '800', color: colors.text, marginBottom: '6px' }}>{fmtFull$(amount)}</div>
      <div style={{ fontSize: '11px', color: '#6b7280' }}>{note}</div>
    </div>
  );
}

export default function AdminHistoricals() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeQuick, setActiveQuick] = useState('All Time');

  const load = useCallback(async (sd, ed) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (sd && ed) { params.start_date = sd; params.end_date = ed; }
      const res = await axios.get(`${API}/api/admin/borrowers/historicals`, { headers, params });
      setLoans(res.data.loans || []);
      setSummary(res.data.summary || null);
      setMonthly(res.data.monthly_breakdown || []);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load('', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyQuick = (q) => {
    setActiveQuick(q.label);
    if (!q.days) {
      setStartDate(''); setEndDate('');
      load('', '');
    } else {
      const end = new Date();
      const start = new Date(Date.now() - q.days * 86400000);
      const sd = start.toISOString().slice(0, 10);
      const ed = end.toISOString().slice(0, 10);
      setStartDate(sd); setEndDate(ed);
      load(sd, ed);
    }
  };

  const applyFilter = () => { setActiveQuick(''); load(startDate, endDate); };

  const historicalsCSVColumns = [
    { label: 'Loan #', key: 'loan_number' },
    { label: 'Borrower', key: 'borrower_name' },
    { label: 'Closed Date', accessor: (r) => r.closed_date ? new Date(r.closed_date).toLocaleDateString() : '' },
    { label: 'Address', accessor: (r) => (r.property_address || '').replace(/,/g, ' ') },
    { label: 'Type', key: 'property_type' },
    { label: 'Purchase Price', key: 'purchase_price' },
    { label: 'Down %', key: 'down_payment_pct' },
    { label: 'Loan Amount', key: 'loan_amount' },
    { label: 'LTV', key: 'ltv' },
    { label: 'DTI', key: 'dti_back' },
    { label: 'FICO', accessor: r => { const s = getCreditScore(r); return s.isActual ? `${s.display} (actual)` : (r.fico_range || ''); } },
    { label: 'Rate', key: 'interest_rate' },
    { label: 'Program Fee', accessor: (r) => String(Math.round(r.program_fee || 0)) },
    { label: 'RE Commission', accessor: (r) => String(Math.round(r.re_commission || 0)) },
    { label: 'Total Revenue', accessor: (r) => String(Math.round(r.total_revenue || 0)) },
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Historicals</h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Closed loan performance</p>
          </div>
          <ExportToolbar
            disabled={loans.length === 0}
            onExportCSV={() => exportCSV(loans, historicalsCSVColumns, `doorly-historicals-${todayStr()}`)}
            onExportJSON={() => exportJSON({ report: 'Historicals', generated: new Date().toISOString(), summary, loans }, `doorly-historicals-${todayStr()}`)}
          />
        </div>

        {/* Date range */}
        <div style={{ ...card, marginBottom: '20px', padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {QUICK_RANGES.map((q) => (
              <button key={q.label} onClick={() => applyQuick(q)}
                style={{ padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid',
                  backgroundColor: activeQuick === q.label ? '#111827' : '#fff',
                  color: activeQuick === q.label ? '#fff' : '#374151',
                  borderColor: activeQuick === q.label ? '#111827' : '#e5e7eb' }}>
                {q.label}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setActiveQuick(''); }}
                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>to</span>
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setActiveQuick(''); }}
                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
              <button onClick={applyFilter}
                style={{ padding: '6px 14px', borderRadius: '8px', backgroundColor: '#3a6ad9', color: '#fff',
                  border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Apply
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '80px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#ff082f' }}>{error}</div>
        ) : (
          <>
            {/* KPI cards */}
            {summary && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { label: 'Loans Closed', value: summary.total_loans },
                    { label: 'Purchase Volume', value: fmt$(summary.total_purchase_price) },
                    { label: 'Loan Volume', value: fmt$(summary.total_loan_amount) },
                    { label: 'Total Revenue', value: fmt$(summary.total_revenue) },
                    { label: 'Avg LTV', value: fmtPct(summary.avg_ltv) },
                    { label: 'Avg Rate', value: fmtPct(summary.avg_interest_rate) },
                  ].map((k) => (
                    <div key={k.label} style={{ ...card, padding: '14px 16px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{k.label}</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{k.value}</div>
                    </div>
                  ))}
                </div>

                {/* Revenue stream cards */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                  {/* ⚠️ BOTH NOTES WENT STALE WITH THE SINGLE-OPTION COLLAPSE.
                      Program Fees read "2% of borrower purchase price (Option 1
                      loans only)" — the rate is per-loan now and "Option 1 only"
                      describes a retired structure. Resale Spread read "5% or
                      10%", which names the two retired options as though they
                      were the choice on offer. Both figures are summed from each
                      loan's own stored values, so neither note should name a
                      rate at all. */}
                  <RevenueCard title="Program Fees" amount={summary.total_program_fees}
                    note="Summed per loan, at each loan's own SmartEstimate rate" color="blue" />
                  <RevenueCard title="RE Commission" amount={summary.total_re_commission}
                    note="3% of Doorly purchase price" color="green" />
                </div>
              </>
            )}

            {/* Monthly performance chart */}
            {monthly.length > 0 && (
              <div style={{ ...card, marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Monthly Performance</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={monthly} margin={{ left: 0, right: 20, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right"
                      tickFormatter={(v) => fmt$(v)} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => name === 'Loan Count' ? [v, name] : [fmt$(v), name]} />
                    <Legend />
                    <Line yAxisId="right" type="monotone" dataKey="purchase_price" stroke="#3a6ad9" name="Purchase Volume" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#acf44e" name="Revenue" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="left" type="monotone" dataKey="count" stroke="#3a6ad9" name="Loan Count" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Loan table */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Closed Loans ({loans.length})</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Loan #', 'Borrower', 'Closed', 'Address', 'Purchase Price', 'Loan Amount', 'LTV', 'DTI', 'FICO', 'Rate', 'Program Fee', 'RE Commission', 'Total Revenue'].map((h) => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loans.length === 0 ? (
                      <tr>
                        <td colSpan={14} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: '48px' }}>
                          No closed loans in selected range
                        </td>
                      </tr>
                    ) : loans.map((l) => (
                      <tr key={l.id}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={td}>
                          <button onClick={() => navigate(`/admin/borrowers/${l.id}`)}
                            style={{ background: 'none', border: 'none', color: '#3a6ad9', fontWeight: '600', fontSize: '13px', cursor: 'pointer', padding: 0 }}>
                            {l.loan_number || '—'}
                          </button>
                        </td>
                        <td style={td}>{l.borrower_name || '—'}</td>
                        <td style={td}>
                          {l.closed_date ? new Date(l.closed_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td style={{ ...td, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.property_address || '—'}</td>
                        <td style={td}>{fmtFull$(l.purchase_price)}</td>
                        <td style={td}>{fmtFull$(l.loan_amount)}</td>
                        <td style={{ ...td, color: ltvColor(l.ltv), fontWeight: '600' }}>{fmtPct(l.ltv)}</td>
                        <td style={td}>{fmtPct(l.dti_back)}</td>
                        <td style={td}>{(() => { const s = getCreditScore(l); return s.isActual ? <span style={{ fontWeight: '700' }}>{s.display} <span style={{ fontSize: '10px', background: '#acf44e', color: '#1a1a1a', padding: '1px 5px', borderRadius: '4px', fontWeight: '700' }}>✓</span></span> : (l.fico_range || '—'); })()}</td>
                        <td style={td}>{fmtPct(l.interest_rate)}</td>
                        <td style={td}>{fmtFull$(l.program_fee)}</td>
                        <td style={td}>{fmtFull$(l.re_commission)}</td>
                        <td style={{ ...td, fontWeight: '600', color: '#acf44e' }}>{fmtFull$(l.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {loans.length > 0 && summary && (
                    <tfoot>
                      <tr>
                        <td style={tdTotal}>TOTALS</td>
                        <td style={tdTotal} colSpan={3}></td>
                        <td style={tdTotal}>{fmt$(summary.total_purchase_price)}</td>
                        <td style={tdTotal}>{fmt$(summary.total_loan_amount)}</td>
                        <td style={tdTotal}>{fmtPct(summary.avg_ltv)}</td>
                        <td style={tdTotal}>—</td>
                        <td style={tdTotal}>—</td>
                        <td style={tdTotal}>{fmtPct(summary.avg_interest_rate)}</td>
                        <td style={tdTotal}>{fmt$(summary.total_program_fees)}</td>
                        <td style={tdTotal}>{fmt$(summary.total_re_commission)}</td>
                        <td style={{ ...tdTotal, color: '#acf44e' }}>{fmt$(summary.total_revenue)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
